import { ApiCreditcard } from '@/models/api/creditcard'
import { SelectOption, SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { Payment } from '@/models/api/payment/payment'
import { PaymentType } from '@/models/app-config/behavior/types'
import OptionsValidator, { ErrorBag } from '@/services/payment/options-validator'
import { ResolverResource } from '@/services/payment/variable-resolver'
import SelectedPayment from '@spa/components/pages/order/edit/payment/model/selected-payment'

/**
 * 再注文（支払い方法変更）画面におけるユーザーの選択状態を保持する
 *
 * 役割としては初回注文Order.vueにおけるVuexのOrderConfirmStateと同様のものを想定しているが、
 * ReorderPayment.vueにおいては支払い方法しか選択の余地がないのでVuexよりスリムになるはず。
 */
export class ReorderPaymentState {
  static init(): ReorderPaymentState {
    return new ReorderPaymentState({
      selectedPayment: SelectedPayment.empty(),
      selectedCard: null,
      selectedPaymentOptionsValidation: ErrorBag.empty(),
      variableResource: {},
    })
  }

  public readonly selectedPayment: SelectedPayment
  public readonly selectedCard: ApiCreditcard | null
  public readonly selectedPaymentOptionsValidation: ErrorBag
  public readonly variableResource: ResolverResource

  private constructor(params: {
    selectedPayment: SelectedPayment,
    selectedCard: ApiCreditcard | null,
    /**
     * 選択された支払い方法に関するバリデーション結果。
     * ただし現在はコンビニ決済、キャリア決済のバリデーション結果しか入っていない。
     */
    selectedPaymentOptionsValidation: ErrorBag,
    variableResource: ResolverResource,
  }) {
    this.selectedPayment = params.selectedPayment
    this.selectedCard = params.selectedCard
    this.selectedPaymentOptionsValidation = params.selectedPaymentOptionsValidation
    this.variableResource = params.variableResource
  }

  setSelectedPayment(payment: Payment): ReorderPaymentState {
    return new ReorderPaymentState({
      ...this,
      selectedPayment: SelectedPayment.createByPayment(payment),
    })
  }

  setSelectedOptionList(selectedOptionList: SelectOptionList): ReorderPaymentState {
    const selectedPayment = this.selectedPayment.update(selectedOptionList)

    return new ReorderPaymentState({
      ...this,
      selectedPayment,
    })
  }

  unsetSelectedPayment(): ReorderPaymentState {
    return new ReorderPaymentState({
      ...this,
      selectedPayment: null,
    })
  }

  setSelectedCard(card: ApiCreditcard): ReorderPaymentState {
    return new ReorderPaymentState({
      ...this,
      selectedCard: card,
    })
  }

  unsetSelectedCard(): ReorderPaymentState {
    return new ReorderPaymentState({
      ...this,
      selectedCard: null,
    })
  }

  toggleOption(option: SelectOption): ReorderPaymentState {
    const selectedPayment = this.selectedPayment.toggleOption(option)

    return new ReorderPaymentState({
      ...this,
      selectedPayment,
    })
  }

  /**
   * コンビニ決済のコンビニ種別、キャリア決済のキャリア種別のようなユーザー選択のオプションについて、
   * 必須項目の検査などを行いバリデーション結果をselectedPaymentOptionsValidationに詰めたstateを返す
   */
  validatePaymentOptions(): ReorderPaymentState {
    if (!this.selectedPayment.payment) {
      return this
    }

    const errors = (new OptionsValidator(
      this.selectedPayment.payment.parameters,
      this.selectedPayment.optionList
    )).validate()

    return new ReorderPaymentState({
      ...this,
      selectedPaymentOptionsValidation: errors,
    })
  }

  addVariableResource(params: Partial<ResolverResource>): ReorderPaymentState {
    return new ReorderPaymentState({
      ...this,
      variableResource: {
        ...this.variableResource,
        ...params,
      },
    })
  }

  get paymentType(): PaymentType | undefined {
    return this.selectedPayment.payment ?
      this.selectedPayment.payment.id :
      undefined
  }
}
